import { Loader2 } from "lucide-react";
import styles from "./LoadingButton.module.css";

/**
 * LoadingButton — drop-in replacement for any <button> that fires an API call.
 *
 * Usage:
 *   <LoadingButton
 *     loading={saving}
 *     className={styles.btnPrimary}
 *     onClick={handleSave}
 *     icon={<Save size={13} />}
 *   >
 *     Save
 *   </LoadingButton>
 *
 * Props:
 *   loading      {boolean}  – show spinner + disable the button
 *   loadingText  {string}   – text shown while loading (defaults to children)
 *   icon         {node}     – icon shown when NOT loading (replaces icon in children)
 *   disabled     {boolean}  – additional disabled condition
 *   spinnerSize  {number}   – Loader2 size (default 13)
 *   className    {string}   – forwarded to <button> (use your existing style class)
 *   All other standard <button> props are forwarded.
 */
const LoadingButton = ({
  children,
  loading = false,
  loadingText,
  icon,
  disabled = false,
  spinnerSize = 13,
  className = "",
  type = "button",
  onClick,
  style,
  ...rest
}) => {
  const isDisabled = loading || disabled;

  return (
    <button
      type={type}
      className={[className, loading ? styles.busy : ""].filter(Boolean).join(" ")}
      disabled={isDisabled}
      onClick={onClick}
      style={style}
      {...rest}
    >
      {loading ? (
        <>
          <Loader2 size={spinnerSize} className={styles.spinner} />
          {loadingText ?? children}
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </button>
  );
};

export default LoadingButton;
